import React, { useEffect, useState } from "react";
import styles from "./fbm-listings-search.module.css";
import TextInput from "@/components/forms/TextInput/TextInput";
import IconSearch from "@/components/icons/IconSearch";
import { useFocus } from "@/hooks/useFocus";
import { Product, useInventoryContext } from "@/contexts/InventoryContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { getCatalogItems } from "@/api/products";
import Select from "@/components/forms/Select/Select";
import Button from "@/components/forms/Button/Button";
import ProductListItemSkeleton from "@/components/ProductListItemSkeleton/ProductListItemSkeleton";
import FbmListingsSearchItem from "../FbmListingsSearchItem/FbmListingsSearchItem";
import FbmAddListingDrawer from "../FbmAddListingDrawer/FbmAddListingDrawer";
import Flex from "@/components/Flex/Flex";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Pagination from "@/components/Pagination/Pagination";

export enum SearchMode {
  allProducts = "All products",
  inventoryProducts = "Active products",
  amazonCatalog = "Amazon catalog"
}

const cachedCatalogSearchResults: { [key: string]: Product[] } = {};
const cachedSalesRanks: { [key: string]: { salesRank?: number; category?: string } } = {};

let searchTimeoutId;

type FbmListingsSearchProps = {};

const FbmListingsSearch = ({ }: FbmListingsSearchProps) => {
  const { isFetchingInventory, fbmInventory, getSalesRanksForASINs } = useInventoryContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const [searchMode, setSearchMode] = useState(SearchMode.allProducts);
  const [searchInputRef, setSearchInputFocus] = useFocus<HTMLInputElement>();
  const [searchInputText, setSearchInputText] = useState<string>("");
  const [defaultSku, setDefaultSKu] = useState<string>("");
  const [isLoadingSearchResults, setIsLoadingSearchResults] = useState<boolean>(false);
  const [inventoryResults, setInventoryResults] = useState<Product[]>([]);
  const [catalogResults, setCatalogResults] = useState<Product[]>([]);
  const [inventoryView, setInventoryView] = useState<Product[]>([]);
  const [catalogView, setCatalogView] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [addedProduct, setAddedProduct] = useState<Product>();
  const [replenish, setReplenish] = useState<boolean>(false);
  const [sellerItemIndex, setSellerItemIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<"form" | "details" | "offers">("form");

  const setAddedItemData = (product: Partial<Product>, sellerItemIndex: number, defaultSkuValue: string, replenish: boolean) => {
    setSellerItemIndex(sellerItemIndex);
    setAddedProduct({ ...product } as Product);
    setDefaultSKu(defaultSkuValue);
    setReplenish(replenish);
  };

  const finalizeSearchResults = async (inventorySearchResults: Product[], catalogSearchResults: Product[]) => {
    const pageSize = 10;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = (currentPage - 1) * pageSize + pageSize;
    let inventoryView = [];

    if (inventorySearchResults.length > 0) {
      setIsLoadingSearchResults(true);

      inventoryView = inventorySearchResults.slice(startIndex, Math.min(endIndex, inventorySearchResults.length));
      try {
        const salesRanks = await getSalesRanksForASINs(inventoryView.map(i => i.asin));
        inventoryView.forEach(product => {
          const salesRank = salesRanks.find(salesRank => salesRank.asin === product.asin);
          if (salesRank) {
            product.salesRank = salesRank.salesRank;
            product.category = salesRank.category;
            product.categoryLink = salesRank.link;
          }
        });
      } catch (e: any) {
        if (handleErrorResponseGenerically(e)) {
          return;
        }
      }
    }

    setInventoryView(inventoryView);
    if (inventoryView.length < 10) {
      const range = 10 - inventoryView.length;
      const catalogStart = Math.max(0, startIndex - inventorySearchResults.length);
      setCatalogView(catalogSearchResults.slice(catalogStart, catalogStart + range));
    } else {
      setCatalogView([]);
    }

    setInventoryResults(inventorySearchResults);
    setCatalogResults(catalogSearchResults);

    setIsLoadingSearchResults(false);
  };

  const debounceSearch = async (searchText: string, searchMode: SearchMode) => {

    setIsLoadingSearchResults(true);
    clearTimeout(searchTimeoutId);

    let searchTerm = searchText.trim();
    setCurrentPage(1);

    if (searchTerm.length === 0) {
      setIsLoadingSearchResults(false);

      await finalizeSearchResults([], []);
      return;
    }

    const originalSearchTerm = searchTerm;
    searchTerm = searchTerm.toLowerCase();

    const catalogSearch = async (searchTerm: string, inventoryResults: Product[]): Promise<Product[]> => {
      const cachedResults = cachedCatalogSearchResults[searchTerm];
      if (cachedResults) {
        const filteredItems = cachedResults.filter(item => !inventoryResults.find(inventoryItem => item.asin === inventoryItem.asin));
        return filteredItems;
      }

      if (/^[, ]+$/.test(searchTerm)) {
        return [];
      }

      try {
        const [catalogResults, nextToken] = await getCatalogItems(searchTerm);
        cachedCatalogSearchResults[searchTerm] = catalogResults;

        //Filter out duplicate ASINS.
        const filteredItems = catalogResults.filter(item => !inventoryResults.find(inventoryItem => item.asin === inventoryItem.asin));
        return filteredItems;
      } catch (e: any) {
        if (handleErrorResponseGenerically(e)) return [];
      }
    };

    searchTimeoutId = setTimeout(async () => {
      setCatalogResults([]);
      setInventoryResults([]);

      if (searchMode === SearchMode.allProducts || searchMode === SearchMode.inventoryProducts) {
        const inventoryResults: Product[] = [];
        for (let i = 0; i < fbmInventory.length; i++) {
          const inventoryItem: Product = fbmInventory[i];
          const searchTermValues = searchTerm.split(" "); //Split on spaces - potential future upgrade split multiple characters.
          const foundItem = searchTermValues.filter(term => inventoryItem.searchString.includes(term)); //Filter out terms not found in search string.
          if (foundItem.length === searchTermValues.length) inventoryResults.push(inventoryItem); //Push inv item if all terms were present in search string.
        }

        let catalogResults = [];
        if (searchMode === SearchMode.allProducts) {
          catalogResults = await catalogSearch(originalSearchTerm, inventoryResults);
        }
        await finalizeSearchResults(inventoryResults, catalogResults);
      } else {
        const catalogResults = await catalogSearch(originalSearchTerm, []);
        await finalizeSearchResults([], catalogResults);
      }
    }, 350);
  };

  useEffect(() => {
    finalizeSearchResults(inventoryResults, catalogResults);
  }, [currentPage]);

  if (isFetchingInventory) {
    return (
      <div className={styles.listingSearchContainer}>
        <Flex align="center" gap={4}>
          <LoadingSpinner /> Loading your inventory...
        </Flex>
      </div>
    );
  }

  return (
    <>
      <div className={styles.listingSearchContainer}>
        <Flex gap={4}>
          <TextInput
            ref={searchInputRef}
            placeholder="Search by product name, SKU, FNSKU, ASIN, UPC, ISBN, EAN, or GTIN"
            prefix={<Flex align="center" style={{ marginRight: "1ch" }}><IconSearch /></Flex>}
            value={searchInputText}
            onChange={v => {
              setSearchInputText(v);
              debounceSearch(v, searchMode);
            }}
            maxLength={300}
            inputAutofocus
          />

          <Select
            anchor="right"
            options={[SearchMode.allProducts, SearchMode.inventoryProducts, SearchMode.amazonCatalog]}
            selectionRender={(option) => {
              switch (option) {
                case SearchMode.allProducts:
                  return "All products";

                case SearchMode.inventoryProducts:
                  return "Inventory products";

                case SearchMode.amazonCatalog:
                  return "Catalog products";
              }
            }}
            optionRender={(option) => {
              switch (option) {
                case SearchMode.allProducts:
                  return "All products";

                case SearchMode.inventoryProducts:
                  return (
                    <Flex direction="column">
                      <span>
                        Active products
                      </span>
                      <span className="weak">
                        Products currently in your inventory
                      </span>
                    </Flex>
                  );

                case SearchMode.amazonCatalog:
                  return (
                    <Flex direction="column">
                      <span>
                        Amazon catalog
                      </span>
                      <span className="weak">
                        Products from the entire Amazon catalog
                      </span>
                    </Flex>
                  );
              }
            }}
            value={searchMode}
            onChange={(option) => {
              setSearchMode(option);
              debounceSearch(searchInputText, option);
            }}
          />
        </Flex>

        <div className={styles.productListContainer}>
          {searchInputText === "" && (
            <Flex
              direction="column"
              className={styles.emptyBox}
            >
              <img src="/images/search.svg" height="100" />
              <p>{`Search by typing a search query above.`}</p>
            </Flex>
          )}

          {!isLoadingSearchResults && catalogResults.length === 0 && inventoryResults.length === 0 && searchInputText !== "" &&
            <Flex
              direction="column"
              className={styles.emptyBox}
            >
              <span className="strong">
                {`No product found matching "${searchInputText}"`}
              </span>
              <img src="/images/search.svg" height="100" />
              <p>{`Try adjusting your filters to find the products you're looking for`}</p>
              <Button onClick={() => { setSearchInputText(""); setSearchInputFocus(); }} type="secondary">
                Clear search
              </Button>
            </Flex>
          }

          {isLoadingSearchResults && (
            <>
              <ProductListItemSkeleton />
              <ProductListItemSkeleton />
              <ProductListItemSkeleton />
            </>
          )}

          {!isLoadingSearchResults && searchInputText !== "" && inventoryView.map((item, index) => {
            return (
              <FbmListingsSearchItem
                key={index}
                product={item}
                onAddListing={setAddedItemData}
                setActiveTab={setActiveTab}
              />
            );
          })}

          {!isLoadingSearchResults && searchInputText !== "" && catalogView.map((product, index) => (
            <FbmListingsSearchItem
              key={index}
              product={product}
              onAddListing={setAddedItemData}
              setActiveTab={setActiveTab}
            />
          ))}
        </div>

        {((inventoryResults.length !== 0 || catalogResults.length !== 0) && ((inventoryResults.length + catalogResults.length) > 10)) && (
          <Pagination
            sticky
            showPageSizeOptions={false}
            total={inventoryResults.length + catalogResults.length}
            pageSize={10}
            onChangePageSize={() => { }}
            page={currentPage}
            onChangePage={(e) => setCurrentPage(e)}
          />
        )}
      </div>

      {addedProduct && (
        <FbmAddListingDrawer
          defaultSku={defaultSku}
          product={addedProduct}
          replenish={replenish}
          sellerDataIndex={sellerItemIndex}
          onClose={() => {
            setSearchInputFocus();
            setAddedProduct(undefined);
            setReplenish(false);
          }}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
    </>
  );
};

export default FbmListingsSearch;